import React, { useEffect, useState } from 'react';
import { IoTrashOutline } from 'react-icons/io5';
import { useSelector } from 'react-redux';
import { Box, Flex, Icon, IconButton } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import DropdownMenu from '../../../../../../components/DropdownMenu';
import { findSelected } from '../../../../../../components/DropdownMenu/util';
import MultiSelectDropdown from '../../../../../../components/MultiSelectDropdown';
import { selectAdminPageRoles } from '../../../../../../redux/modules/adminPage';
import { selectCompanyDepartments } from '../../../../../../redux/modules/company';
import { selectUserId } from '../../../../../../redux/modules/user';
import colors from '../../../../../../styles/theme/foundation/colors';
import { arraysEqual } from '../../../../../../utils/arrayUtil';

const DepartmentRoleBox = ({ user, role, availableRoles, onDelete, onChange, disableRoleChange }) => {
  const roles = useSelector(selectAdminPageRoles);
  const currentLoggedInUserId = useSelector(selectUserId);
  const isCurrentUser = user?.userId === currentLoggedInUserId;
  const departments = useSelector(selectCompanyDepartments);
  const [stagedDepartmentIds, setStagedDepartmentIds] = useState(role.departmentIds || []);
  const [stagedRoleId, setStagedRoleId] = useState(role?.roleId);
  const [previousRoleId, setPreviousRoleId] = useState(role?.roleId);

  const departmentOptions = departments.filter(d => !d.archivedAt).map(({ departmentName, departmentId }) => ({
    displayName: departmentName,
    id: departmentId
  }));

  const handleChange = () => {
    if (stagedRoleId !== role.roleId || !arraysEqual(stagedDepartmentIds, role.departmentIds)) {
      onChange({
        roleId: stagedRoleId,
        departmentIds: stagedDepartmentIds,
        previousRoleId
      });
    }
  };

  useEffect(() => {
    handleChange();
  }, [stagedRoleId]);

  const handleRoleChange = selected => {
    setPreviousRoleId(stagedRoleId);
    setStagedRoleId(Number(selected.value));
  };

  const handleDepartmentChange = selected => {
    setStagedDepartmentIds(selected.map(d => d.id));
  };

  const statedDepartmentValues = stagedDepartmentIds.map(d => {
    const department = departmentOptions.find(dept => dept.id === d);
    return {
      displayName: department.displayName,
      id: department.id
    };
  });

  return (
    <Box bg="primaryScale.50" borderRadius="10px" height="146px" mt={0} w="100%">
      <Flex alignItems="center" direction="row" height="100%" justifyContent="center">
        <Box bg="primaryScale.50" borderRadius="md" height="146px" mt={0} w="100%">
          <Flex alignItems="center" direction="row" height="100%" justifyContent="space-around" w="100%">
            <Flex
              alignItems="center"
              direction="column"
              height="100%"
              justifyContent="space-evenly"
              w="100%"
            >
              <DropdownMenu
                isDisabled={isCurrentUser || disableRoleChange}
                labelKey="displayName"
                options={availableRoles}
                placeholder="User roles"
                selected={
                  stagedRoleId !== null
                    ? findSelected(roles, stagedRoleId).displayName
                    : ''
              }
                width="300px"
                onSelect={handleRoleChange}
              />
              <Box w="300px">
                <MultiSelectDropdown
                  data={departmentOptions}
                  displayLength={1}
                  isDisabled={isCurrentUser}
                  labelKey="displayName"
                  placeholder="Departments"
                  showLabel={false}
                  values={statedDepartmentValues}
                  onChange={handleDepartmentChange}
                  onClose={handleChange}
                />
              </Box>
            </Flex>
          </Flex>
        </Box>
        <IconButton
          _focus={{ outline: 'none' }}
          icon={<Icon as={IoTrashOutline} color="greyScale.800" height="24px" value={role.roleId} w="24px" />}
          value={role.roleId}
          variant="textGrey"
          onClick={() => onDelete(role.roleId, role.departmentIds)}
        />
      </Flex>
    </Box>
  );
};

const SelectedDepartmentRole = ({ children }) => (
  <Box
    background={colors.greyScale['100']}
    borderRadius="md"
    color={colors.greyScale['900']}
    maxHeight="60px"
    overflowY="auto"
    padding="10px 14px"
    textStyle="bodyMd"
    width="300px"
  >
    {children}
  </Box>
);

DepartmentRoleBox.propTypes = {
  disableRoleChange: PropTypes.bool,
  user: PropTypes.shape({
    userId: PropTypes.number,
    roles: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      roleId: PropTypes.number,
      displayName: PropTypes.string
    }))
  }),
  role: PropTypes.shape({
    roleId: PropTypes.number,
    departmentIds: PropTypes.arrayOf(PropTypes.number)
  }),
  availableRoles: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    roleId: PropTypes.number,
    displayName: PropTypes.string
  })),
  onDelete: PropTypes.func,
  onChange: PropTypes.func
};

SelectedDepartmentRole.propTypes = { children: PropTypes.node };

export default DepartmentRoleBox;
